import {
  BookedTravelerCollectionInt,
  CabinUpgradeAncillary,
  PremiereUpgradeInt,
  SegmentInt,
  flightsNewInfoInt,
  iropEligibilityInt
} from "@interfaces/*";
import { CHECKIN_STATUS, FARES_FAMILY } from "@app/enums";
import { Passenger } from "@models/passenger.model";

export class Segment {
  aircraftType: string;
  arrivalAirport: string;
  arrivalCityName: string;
  arrivalTime: string;
  checkinStatus: CHECKIN_STATUS;
  departureAirport: string;
  departureCityName: string;
  departureDateTime: Date;
  departureTime: string;
  fareBasisCode: string;
  fareFamily: FARES_FAMILY;
  flightStatus: string;

  flightNumber: string;
  isPremierAvailable: boolean;
  layoverHours: number;
  layoverMinutes: number;
  layoverToNextSegmentsInHours: number;
  layoverToNextSegmentsInMinutes: number;
  premiereUpgrade: PremiereUpgradeInt;
  segmentCode: string;
  upgradingPassengers: Passenger[] = [];
  operatingCarrier: string;
  estimatedArrivalTime: Date;
  estimatedDepartureTime: Date;
  boardingTime: string;
  stops: any[];
  
  itineraryPartType: string;
  flightsNewInfo: flightsNewInfoInt;
  iropEligibility: iropEligibilityInt;
  isCancelOag: boolean;
  isCancelOds: boolean;
  isChangeOag: boolean;
  isChangeOds: boolean;
  codeShare: boolean;

  constructor(data?:{cart?: BookedTravelerCollectionInt, segment: SegmentInt}) {
    if(data){
      const {cart,segment} = data;

      this.aircraftType = segment.aircraftType;
      this.arrivalAirport = segment.arrivalAirport;
      this.arrivalTime = this.getTime(segment.arrivalDateTime);
      this.checkinStatus = segment.checkinStatus;
      this.departureAirport = segment.departureAirport;
      this.departureDateTime = segment.departureDateTime;
      this.departureTime = this.getTime(segment.departureDateTime);
      this.fareBasisCode = segment.fareBasisCode;
      this.fareFamily = segment.fareFamily;
      this.flightNumber = segment.operatingCarrier + " " + segment.operatingFlightCode;
      this.flightStatus = segment.flightStatus;
      this.layoverToNextSegmentsInMinutes = segment.layoverToNextSegmentsInMinutes;
      this.segmentCode = segment.segmentCode;
      this.operatingCarrier = segment.operatingCarrier;
      this.estimatedArrivalTime = segment.estimatedArrivalTime;
      this.estimatedDepartureTime = segment.estimatedDepartureTime;
      this.boardingTime = segment.boardingTime;
      this.stops = segment.stops;

      this.flightsNewInfo = segment.flightsNewInfo;
      this.isCancelOag = segment.isCancelOag;
      this.isCancelOds = segment.isCancelOds;
      this.isChangeOag = segment.isChangeOag;
      this.isChangeOds = segment.isChangeOds;

      if (this.layoverToNextSegmentsInMinutes && this.layoverToNextSegmentsInMinutes > 0) {
        this.layoverHours = Math.floor(this.layoverToNextSegmentsInHours);
        this.layoverMinutes = Number(this.layoverToNextSegmentsInMinutes) % 60;
        this.layoverToNextSegmentsInHours = Number((this.layoverToNextSegmentsInMinutes / 60).toFixed(2));
      }

      this.arrivalCityName = "";
      this.departureCityName = "";
      this.isPremierAvailable = segment.isPremierAvailable;
      this.premiereUpgrade = this.buildPremiereUpgrade(cart, this.segmentCode, this.isPremierAvailable);
    }
  }

  hasPremiereUpgradeOffer() {
    if (!this.premiereUpgrade.available) {
      return false;
    }
    if (this.premiereUpgrade.availableSeats <= 0) {
      return false;
    }
    return true;
  };

  hasCustomersUpgradingPremiere() {
    return this.upgradingPassengers.length > 0;
  };

  removePassengerFromSegmentUpgrade(passengerToRemove) {
    var upgradingPassengers = [];
    passengerToRemove.upgradeSelected = false;
    for (var index = 0; index < this.upgradingPassengers.length; index++) {
      var passenger = this.upgradingPassengers[index];

      if (passenger.id != passengerToRemove.id) {
        upgradingPassengers.push(passenger);
      }
    }
    this.upgradingPassengers = upgradingPassengers;
  };

  removeAllPassengerFromSegmentUpgrade() {
    for (var index = 0; index < this.upgradingPassengers.length; index++) {
      var passenger = this.upgradingPassengers[index];
    }
    this.upgradingPassengers = [];
  }

  preSelectPassengers(passengers) {
    if (passengers.length > this.premiereUpgrade.availableSeats) {
      return;
    }
    for (var index = 0; index < passengers.length; index++) {
      var passenger = passengers[index];
      this.upgradingPassengers.push(passenger);
      passenger.upgradeSelected = true;
    }
  }

  getPremiereUpgradeOfferImage() {
    switch (this.aircraftType) {
      case "787":
      case "789":
        return "images/dreamliner_large_banner2.png";
      case "E70":
      case "E7S":
      case "E90":
      case "E95":
      case "E9S":
        return "images/embraer_large_banner.png";
      default:
        return "images/premiereCabinUpgrade.png";
    }
  }

  passengerIsPremiere(passengerToFind) {
    if (passengerToFind == null) {
      return false;
    }
    for (var index = 0; index < this.upgradingPassengers.length; index++) {
      var passenger = this.upgradingPassengers[index];
      if (passenger.id == passengerToFind.id) {
        return true;
      }
    }
    return false;
  }

  getParsedDepartureDateTime() {
    return this.parseApiDate(this.departureDateTime);
  }

  parseApiDate(apiDate) {
    try {
      var tokens = apiDate.split("T");
      var date = this.parseApiYearMonthDay(tokens[0]);
      this.parseApiTime(tokens[1], date);

      return date;
    } catch (e) {
      return null;
    }
  }

  parseApiYearMonthDay(yearMonthDay) {
    var tokens = yearMonthDay.split("-");
    return {
      year: tokens[0],
      month: tokens[1],
      day: tokens[2]
    };
  }

  parseApiTime(time, date) {
    var tokens = time.split(":");
    date.hour = tokens[0];
    date.minute = tokens[1];
    date.second = tokens[2];
  }

  private buildPremiereUpgrade(cart: BookedTravelerCollectionInt, segmentCode: string, isPremierAvailable): PremiereUpgradeInt {
    const premiereUpgrade: PremiereUpgradeInt = {};
    const offer = this.findSegmentsOffer(cart, segmentCode);
    if (offer == null || offer.currency == null || !isPremierAvailable) {
      premiereUpgrade.available = false;
      premiereUpgrade.availableSeats = 0;
    } else {
      premiereUpgrade.available = true;
      premiereUpgrade.currency = offer.currency;
      premiereUpgrade.upgradeCostPerPax = offer.currency.total;
      premiereUpgrade.availableSeats = offer.seatsRemaining;
    }
    return premiereUpgrade;
  }

  private findSegmentsOffer(cart: BookedTravelerCollectionInt, segmentCode: string): CabinUpgradeAncillary {
    if (cart == null || cart.upsellOffers == null || cart.upsellOffers._collection == null) {
      return null;
    }
    for (var index = 0; index < cart.upsellOffers._collection.length; index++) {
      var offer = cart.upsellOffers._collection[index];
      if (segmentCode == offer.segmentCode) {
        return offer;
      }
    }
    return null;
  }
  private getTime(dateTime) {
    var time = '';

    if (dateTime != null) {
      var timeIndex = dateTime.indexOf('T');
      if (timeIndex != -1) {
        timeIndex++;
      }

      time = dateTime.substr(timeIndex, 5);
    }

    return time;
  }

}
